import { Agent, type AgentOutputType } from './agent';
import {
  type CompactionRecord,
  type CompactionStore,
  type CompactTrigger,
  calculateCompactionThreshold,
  estimateTokensForItems,
  FileCompactionStore,
  FileSessionMemoryStore,
  type LocalCompactionOption,
  type ManualCompactRunOption,
  microcompactModelContext,
  type ResolvedLocalCompactionConfig,
  type ResolvedSessionMemoryBackgroundUpdateConfig,
  resolveLocalCompactionConfig,
  resolveSessionMemoryBackgroundUpdateConfig,
  runLocalCompaction,
  type SessionMemoryConfig,
  type SessionMemoryStore,
  SessionMemoryUpdateQueue,
  updateSessionMemory,
} from './compaction';
import { getDefaultModelSettings } from './default-model';
import {
  InputGuardrailTripwireTriggered,
  ModelBehaviorError,
  OutputGuardrailTripwireTriggered,
  UserError,
} from './errors';
import {
  RunAgentUpdatedStreamEvent,
  RunCompactionStreamEvent,
  RunRawModelStreamEvent,
} from './events';
import {
  defineInputGuardrail,
  defineOutputGuardrail,
  type InputGuardrail,
  type InputGuardrailDefinition,
  type InputGuardrailResult,
  type OutputGuardrail,
  type OutputGuardrailDefinition,
  type OutputGuardrailMetadata,
} from './guardrail';
import type { Handoff, HandoffInputFilter } from './handoff';
import {
  extractAllTextOutput,
  type RunItem,
  RunToolCallItem,
  RunToolCallOutputItem,
} from './items';
import { RunHooks } from './lifecycle';
import logger from './logger';
import type { Session, SessionInputCallback } from './memory/session';
import type {
  Model,
  ModelMetadata,
  ModelProvider,
  ModelResponse,
  ModelSettings,
} from './model';
import { getDefaultModelProvider } from './providers';
import { RunResult, StreamedRunResult } from './result';
import { RunContext } from './run-context';
import { RunState } from './run-state';
import { DEFAULT_MAX_TURNS } from './runner/constants';
import {
  applyCallModelInputFilter,
  ServerConversationTracker,
} from './runner/conversation';
import {
  type RunErrorHandlers,
  tryHandleRunError,
} from './runner/error-handlers';
import {
  createGuardrailTracker,
  runOutputGuardrails,
} from './runner/guardrails';
import type { ReasoningItemIdPolicy } from './runner/items';
import { processModelResponseAsync } from './runner/model-outputs';
import { prepareAgentArtifacts } from './runner/model-preparation';
import {
  getResponseWithRetry,
  getStreamedResponseWithRetry,
} from './runner/model-retry';
import {
  adjustModelSettingsForNonGPT5RunnerModel,
  maybeResetToolChoice,
  mergeModelSettings,
  selectModel,
} from './runner/model-settings';
import {
  applyTurnResult,
  handleInterruptedOutcome,
  resumeInterruptedTurn,
} from './runner/run-loop';
import {
  finalizeSandboxRuntime,
  isSandboxRuntimeAgent,
  prepareSandboxInterruptedTurnResume,
  type SandboxMemoryPersistenceContext,
} from './runner/sandbox';
import {
  createSessionTurnContext,
  type SessionTurnContext,
} from './runner/session-graph';
import {
  createSessionPersistenceTracker,
  prepareInputItemsWithSession,
  prepareInputItemsWithSessionAndCompaction,
  saveStreamInputToSession,
  saveStreamResultToSession,
  saveToSession,
} from './runner/session-persistence';
import {
  buildAbortReconciliationInput,
  createStreamAbortReconciliationState,
  getAbortReconciliationPreviousResponseId,
  markAbortReconciliationComplete,
  recordStreamEventForAbortReconciliation,
  shouldReconcileStreamAbort,
} from './runner/stream-reconciliation';
import {
  addStepToRunResult,
  isAbortError,
  streamStepItemsToRunResult,
} from './runner/streaming';
import { applyTraceOverrides, getTracing } from './runner/tracing';
import { prepareTurn } from './runner/turn-preparation';
import { resolveTurnAfterModelResponse } from './runner/turn-resolution';
import type {
  AgentArtifacts,
  CallModelInputFilter,
  PreparedModelCall,
} from './runner/types';
import type { SandboxRunConfig } from './sandbox/client';
import { SandboxRuntimeManager } from './sandbox/runtime';
import type { SandboxRuntimeModel } from './sandbox/runtime/agent-keys';
import type { TracingConfig } from './tracing/config';
import {
  getOrCreateTrace,
  resetCurrentSpan,
  setCurrentSpan,
  withNewSpanContext,
  withTrace,
} from './tracing/context';
import type {
  AgentInputItem,
  AgentOutputItem,
  ResponseStreamEvent,
} from './types';
import { StreamEventResponseCompleted } from './types/protocol';
import { Usage } from './usage';
import { convertAgentOutputTypeToSerializable } from './utils/tools';

/**
 * Common run options shared between streaming and non-streaming execution pathways.
 */
type SharedRunOptions<
  TContext = undefined,
  TAgent extends Agent<any, any> = Agent<any, any>,
> = {
  context?: TContext | RunContext<TContext>;
  maxTurns?: number | null;
  signal?: AbortSignal;
  previousResponseId?: string;
  conversationId?: string;
  session?: Session;
  sessionInputCallback?: SessionInputCallback;
  callModelInputFilter?: CallModelInputFilter;
  toolErrorFormatter?: ToolErrorFormatter;
  reasoningItemIdPolicy?: ReasoningItemIdPolicy;
  tracing?: TracingConfig;
  sandbox?: SandboxRunConfig;
  compaction?: LocalCompactionOption;
  compact?: ManualCompactRunOption;
  /**
   * Error handlers keyed by error kind.
   */
  errorHandlers?: RunErrorHandlers<TContext, TAgent>;
};

/**
 * Options for runs that stream incremental events as the model responds.
 */
export type StreamRunOptions<
  TContext = undefined,
  TAgent extends Agent<any, any> = Agent<any, any>,
> = SharedRunOptions<TContext, TAgent> & {
  /**
   * Whether to stream the run. If true, the run will emit events as the model responds.
   */
  stream: true;
};

/**
 * Options for runs that collect the full model response before returning.
 */
export type NonStreamRunOptions<
  TContext = undefined,
  TAgent extends Agent<any, any> = Agent<any, any>,
> = SharedRunOptions<TContext, TAgent> & {
  /**
   * Run to completion without streaming incremental events; leave undefined or set to `false`.
   */
  stream?: false;
};

/**
 * Options polymorphic over streaming or non-streaming execution modes.
 */
export type IndividualRunOptions<
  TContext = undefined,
  TAgent extends Agent<any, any> = Agent<any, any>,
> = StreamRunOptions<TContext, TAgent> | NonStreamRunOptions<TContext, TAgent>;

type LocalCompactionRuntime = {
  enabled: boolean;
  config: ResolvedLocalCompactionConfig;
  store?: CompactionStore;
  sessionId?: string;
  records: CompactionRecord[];
  sessionMemoryStore?: SessionMemoryStore;
  sessionMemoryUpdateConfig?: ResolvedSessionMemoryBackgroundUpdateConfig;
};

/**
 * 用于配置整个 Agent 运行的各项设置。
 */
export type RunConfig = {
  /**
   * 整个 Agent 运行所使用的模型。如果设置此项，将覆盖每个 Agent 上设置的模型。
   * 下方传入的 modelProvider 必须能够解析此模型名称。
   */
  model?: string | Model;

  /**
   * 在查找字符串形式的模型名称时所使用的模型提供方。默认为 OpenAI。
   */
  modelProvider: ModelProvider;

  /**
   * 配置全局模型设置。任何非 null 的值都将覆盖 Agent 级别的模型设置。
   */
  modelSettings?: ModelSettings;

  /**
   * 应用于所有交接（handoff）的全局输入过滤器。如果设置了 `Handoff.inputFilter`，
   * 则后者优先。输入过滤器允许你编辑发送给新 Agent 的输入内容。
   * 详见 `Handoff.inputFilter` 的文档。
   */
  handoffInputFilter?: HandoffInputFilter;

  /**
   * 在初始运行输入上执行的一系列输入护栏（guardrails）。
   */
  inputGuardrails?: InputGuardrail[];

  /**
   * 在运行最终输出上执行的一系列输出护栏。
   */
  outputGuardrails?: OutputGuardrail<AgentOutputType<unknown>>[];

  /**
   * 是否对此 Agent 运行禁用追踪。如果禁用，则不会对本次运行进行追踪。
   */
  tracingDisabled: boolean;

  /**
   * 是否在追踪中包含潜在的敏感数据（例如：工具调用或大模型生成内容的输入/输出）。
   * 如果为 false，我们仍会为这些事件创建 span，但不会包含敏感数据。
   */
  traceIncludeSensitiveData: boolean;

  /**
   * 运行的名称，用于追踪。应是一个有意义的逻辑名称，
   * 例如 "Code generation workflow" 或 "Customer support agent"。
   */
  workflowName?: string;

  /**
   * 用于追踪的自定义追踪 ID。如不提供，将自动生成一个新的追踪 ID。
   */
  traceId?: string;

  /**
   * 用于追踪的分组标识符，可将同一对话或流程的多个追踪关联起来。
   * 例如，可以使用聊天线程 ID。
   */
  groupId?: string;

  /**
   * 可选的附加元数据字典，将随追踪记录一起包含。
   */
  traceMetadata?: Record<string, string>;

  /**
   * 本次运行的追踪配置。可用于覆盖导出追踪记录时使用的 API 密钥。
   */
  tracing?: TracingConfig;
  /**
   * 当执行到达沙箱 Agent 时使用的沙箱运行时配置。
   */
  sandbox?: SandboxRunConfig;
  compaction?: LocalCompactionOption;

  /**
   * 自定义会话历史与当前轮次输入的合并方式。
   * 如不指定，则历史项会追加到新输入之前。
   */
  sessionInputCallback?: SessionInputCallback;

  /**
   * 在调用模型之前立即调用，允许调用方编辑
   * 将发送给模型的系统指令或输入项。
   */
  callModelInputFilter?: CallModelInputFilter;

  /**
   * 格式化返回给模型的工具错误消息。
   * 返回 `undefined` 时，将回退到 SDK 的默认消息。
   */
  toolErrorFormatter?: ToolErrorFormatter;

  /**
   * 控制如何将运行项转换为后续轮次的模型输入。
   */
  reasoningItemIdPolicy?: ReasoningItemIdPolicy;
};

/**
 * 编排智能体的执行过程，包括护栏约束、工具调用、会话持久化以及链路追踪。
 * 当你希望在多次运行中使用一致的配置时，可复用同一个 `Runner` 实例。
 */
export class Runner extends RunHooks<any, AgentOutputType<unknown>> {
  public readonly config: RunConfig;
  private readonly traceOverrides: {
    traceId?: string;
    workflowName?: string;
    groupId?: string;
    traceMetadata?: Record<string, string>;
    tracingApiKey?: string;
  };
  private readonly localCompactionFailures = new Map<string, number>();
  private readonly sessionMemoryUpdateQueue = new SessionMemoryUpdateQueue();

  constructor(config: Partial<RunConfig> = {}) {
    super();
    this.config = {
      modelProvider: config.modelProvider ?? getDefaultModelProvider(),
      model: config.model,
      modelSettings: config.modelSettings,
      handoffInputFilter: config.handoffInputFilter,
      inputGuardrails: config.inputGuardrails,
      outputGuardrails: config.outputGuardrails,
      tracingDisabled: config.tracingDisabled ?? false,
      traceIncludeSensitiveData: config.traceIncludeSensitiveData ?? true,
      workflowName: config.workflowName ?? 'Agent workflow',
      traceId: config.traceId,
      groupId: config.groupId,
      traceMetadata: config.traceMetadata,
      tracing: config.tracing,
      sandbox: config.sandbox,
      compaction: config.compaction,
      sessionInputCallback: config.sessionInputCallback,
      callModelInputFilter: config.callModelInputFilter,
      toolErrorFormatter: config.toolErrorFormatter,
      reasoningItemIdPolicy: config.reasoningItemIdPolicy,
    };
    this.traceOverrides = {
      ...(config.traceId !== undefined ? { traceId: config.traceId } : {}),
      ...(config.workflowName !== undefined
        ? { workflowName: config.workflowName }
        : {}),
      ...(config.groupId !== undefined ? { groupId: config.groupId } : {}),
      ...(config.traceMetadata !== undefined
        ? { traceMetadata: config.traceMetadata }
        : {}),
      ...(config.tracing?.apiKey !== undefined
        ? { tracingApiKey: config.tracing.apiKey }
        : {}),
    };
    this.inputGuardrailDefs = (config.inputGuardrails ?? []).map(
      defineInputGuardrail,
    );
    this.outputGuardrailDefs = (config.outputGuardrails ?? []).map(
      defineOutputGuardrail,
    );
  }

  async flushSessionMemoryUpdates(): Promise<void> {
    await this.sessionMemoryUpdateQueue.flush();
  }

  /**
   * 从指定智能体开始运行一个工作流。该智能体将循环运行，直到生成最终输出。循环的执行流程如下：
   * 1. 用给定的输入调用该智能体。
   * 2. 如果产生了最终输出（即智能体生成了类型为 `agent.outputType` 的内容），循环终止。
   * 3. 如果发生转交（handoff），则用新的智能体重新执行循环。
   * 4. 否则，执行工具调用（如果有），然后重新执行循环。
   *
   * 在两种情况下，智能体可能会抛出异常：
   * 1. 如果超过了最大轮次（maxTurns），将抛出 MaxTurnsExceeded 异常，除非被捕获处理。
   * 2. 如果触发防护栏的触发线，将抛出 GuardrailTripwireTriggered 异常。
   *
   * 请注意，只有第一个智能体的输入防护栏会被执行。
   *
   * @param agent - 要运行的起始智能体。
   * @param input - 给智能体的初始输入。可以传入字符串或 `AgentInputItem` 数组。
   * @param options - 运行选项，包括流式传输行为、执行上下文和最大轮次。
   * @returns 运行结果。
   */
  run<T extends Agent<any, any>, C = undefined>(
    agent: T,
    input: string | AgentInputItem[] | RunState<C, T>,
    options?: NonStreamRunOptions<C, T>,
  ): Promise<RunResult<C, T>>;
  run<T extends Agent<any, any>, C = undefined>(
    agent: T,
    input: string | AgentInputItem[] | RunState<C, T>,
    options?: StreamRunOptions<C, T>,
  ): Promise<StreamedRunResult<C, T>>;
  async run<T extends Agent<any, any>, C = undefined>(
    agent: T,
    input: string | AgentInputItem[] | RunState<C, T>,
    options: IndividualRunOptions<C, T> = {
      stream: false,
      context: undefined,
    } as IndividualRunOptions<C, T>,
  ): Promise<RunResult<C, T> | StreamedRunResult<C, T>> {
    const resolvedOptions = options ?? { stream: false, context: undefined };
    // Per-run options take precedence over runner defaults for session memory behavior.
    const sessionInputCallback =
      resolvedOptions.sessionInputCallback ?? this.config.sessionInputCallback;
    // Likewise allow callers to override callModelInputFilter on individual runs.
    const callModelInputFilter =
      resolvedOptions.callModelInputFilter ?? this.config.callModelInputFilter;
    // Per-run callback can override runner-level tool error formatting defaults.
    const toolErrorFormatter =
      resolvedOptions.toolErrorFormatter ?? this.config.toolErrorFormatter;
    const reasoningItemIdPolicy = resolvedOptions.reasoningItemIdPolicy;
    const hasCallModelInputFilter = Boolean(callModelInputFilter);
    const tracingConfig = resolvedOptions.tracing ?? this.config.tracing;
    const traceOverrides = {
      ...this.traceOverrides,
      ...(resolvedOptions.tracing?.apiKey !== undefined
        ? { tracingApiKey: resolvedOptions.tracing.apiKey }
        : {}),
    };
    const effectiveOptions = {
      ...resolvedOptions,
      sessionInputCallback,
      callModelInputFilter,
      toolErrorFormatter,
      reasoningItemIdPolicy,
    };
    const resumingFromState = input instanceof RunState;
    const preserveTurnPersistenceOnResume =
      resumingFromState &&
      (input as RunState<C, T>)._currentTurnInProgress === true;
    const resumedConversationId = resumingFromState
      ? (input as RunState<C, T>)._conversationId
      : undefined;
    const resumedPreviousResponseId = resumingFromState
      ? (input as RunState<C, T>)._previousResponseId
      : undefined;
    const serverManagesConversation =
      Boolean(effectiveOptions.conversationId ?? resumedConversationId) ||
      Boolean(effectiveOptions.previousResponseId ?? resumedPreviousResponseId);
    const resolvedCompactionConfig = resolveLocalCompactionConfig(
      effectiveOptions.compaction,
      this.config.compaction,
    );
    const localCompactionRuntime: LocalCompactionRuntime = {
      enabled: resolvedCompactionConfig.enabled && !serverManagesConversation,
      config: resolvedCompactionConfig,
      records: [],
    };
    // When the server tracks conversation history we defer to it for previous turns so local session
    // persistence can focus solely on the new delta being generated in this process.
    const session = effectiveOptions.session;
    const sessionPersistence = createSessionPersistenceTracker({
      session,
      hasCallModelInputFilter,
      persistInput: saveStreamInputToSession,
      resumingFromState,
    });

    let preparedInput: typeof input = input;
    if (!(preparedInput instanceof RunState)) {
      if (localCompactionRuntime.enabled && session) {
        localCompactionRuntime.sessionId = await session.getSessionId();
        localCompactionRuntime.store =
          resolvedCompactionConfig.store ?? new FileCompactionStore();
        localCompactionRuntime.records =
          await localCompactionRuntime.store.getRecords(
            localCompactionRuntime.sessionId,
          );
        const sessionMemoryConfig = resolvedCompactionConfig.sessionMemory;
        localCompactionRuntime.sessionMemoryStore =
          resolveSessionMemoryStore(sessionMemoryConfig);
        localCompactionRuntime.sessionMemoryUpdateConfig =
          resolveSessionMemoryBackgroundUpdateConfig(sessionMemoryConfig);
      }

      const prepared = localCompactionRuntime.enabled
        ? await prepareInputItemsWithSessionAndCompaction(
            preparedInput,
            session,
            localCompactionRuntime.records,
            sessionInputCallback,
            {
              reasoningItemIdPolicy,
            },
          )
        : await prepareInputItemsWithSession(
            preparedInput,
            session,
            sessionInputCallback,
            {
              // When the server tracks conversation state we only send the new turn inputs;
              // previous messages are recovered via conversationId/previousResponseId.
              includeHistoryInPreparedInput: !serverManagesConversation,
              preserveDroppedNewItems: serverManagesConversation,
              reasoningItemIdPolicy,
            },
          );
      if (serverManagesConversation && session) {
        // When the server manages memory we only persist the new turn inputs locally so the
        // conversation service stays the single source of truth for prior exchanges.
        const sessionItems = prepared.sessionItems;
        if (sessionItems && sessionItems.length > 0) {
          preparedInput = sessionItems;
        } else {
          preparedInput = prepared.preparedInput;
        }
      } else {
        preparedInput = prepared.preparedInput;
      }
      sessionPersistence?.setPreparedItems(prepared.sessionItems);
      sessionPersistence?.setTurnContext(
        await createSessionTurnContext({
          session,
          userItems: prepared.sessionItems,
        }),
      );
    }
    // Streaming runs persist the input asynchronously, so track a one-shot helper
    // that can be awaited from multiple branches without double-writing.
    const ensureStreamInputPersisted =
      sessionPersistence?.buildPersistInputOnce(serverManagesConversation);

    const executeRun = async () => {
      if (effectiveOptions.stream) {
        const streamResult = await this.#runIndividualStream(
          agent,
          preparedInput,
          effectiveOptions,
          ensureStreamInputPersisted,
          sessionPersistence?.recordTurnItems,
          preserveTurnPersistenceOnResume,
          {
            sdkSessionId: async () => await session?.getSessionId(),
            inputOverride: () => sessionPersistence?.getItemsForPersistence(),
          },
          localCompactionRuntime,
          sessionPersistence?.getTurnContext(),
        );
        return streamResult;
      }
      const runResult = await this.#runIndividualNonStream(
        agent,
        preparedInput,
        effectiveOptions,
        sessionPersistence?.recordTurnItems,
        preserveTurnPersistenceOnResume,
        {
          sdkSessionId: async () => await session?.getSessionId(),
          inputOverride: () => sessionPersistence?.getItemsForPersistence(),
        },
        localCompactionRuntime,
      );
      // See note above: allow sessions to run for callbacks/state but skip writes when the server
      // is the source of truth for transcript history.
      if (sessionPersistence && !serverManagesConversation) {
        await saveToSession(
          session,
          sessionPersistence.getItemsForPersistence(),
          runResult,
          {
            compactionStore: localCompactionRuntime.store,
            compactionSessionId: localCompactionRuntime.sessionId,
            turnContext: sessionPersistence.getTurnContext(),
          },
        );
        this.#scheduleSessionMemoryUpdate({
          agent: runResult.state._currentAgent,
          session,
          localCompactionRuntime,
        });
      }
      return runResult;
    };

    if (preparedInput instanceof RunState && preparedInput._trace) {
      const applied = applyTraceOverrides(
        preparedInput._trace,
        preparedInput._currentAgentSpan,
        traceOverrides,
      );
      preparedInput._trace = applied.trace;
      preparedInput._currentAgentSpan = applied.currentSpan;
      return withTrace(preparedInput._trace, async () => {
        if (preparedInput._currentAgentSpan) {
          setCurrentSpan(preparedInput._currentAgentSpan);
        }
        return executeRun();
      });
    }
    return getOrCreateTrace(async () => executeRun(), {
      traceId: this.config.traceId,
      name: this.config.workflowName,
      groupId: this.config.groupId,
      metadata: this.config.traceMetadata,
      // Per-run tracing config overrides exporter defaults such as environment API key.
      tracingApiKey: tracingConfig?.apiKey,
    });
  }

  async compactSession<TAgent extends Agent<any, any>>(
    agent: TAgent,
    options: {
      session: Session;
      context?: unknown | RunContext<unknown>;
      instructions?: string;
      trigger?: CompactTrigger;
      compaction?: LocalCompactionOption;
      signal?: AbortSignal;
    },
  ) {
    const config = resolveLocalCompactionConfig(
      options.compaction ?? true,
      this.config.compaction,
    );
    const sessionId = await options.session.getSessionId();
    const store = config.store ?? new FileCompactionStore();
    const records = await store.getRecords(sessionId);
    const history = await options.session.getItems();
    if (history.length === 0) {
      throw new UserError('Cannot compact an empty session.');
    }
    const sessionMemoryStore = resolveSessionMemoryStore(config.sessionMemory);
    const sessionMemoryUpdateConfig =
      resolveSessionMemoryBackgroundUpdateConfig(config.sessionMemory);
    if (sessionMemoryStore && sessionMemoryUpdateConfig) {
      await this.sessionMemoryUpdateQueue.waitForIdle(
        sessionId,
        sessionMemoryUpdateConfig.waitForUpdateMs,
      );
    }
    const targetModel = await this.#resolveModelForAgent(agent);
    const compactionConfig = applyModelMetadataToCompactionConfig(
      config,
      targetModel.modelMetadata,
    );
    const model = await this.#resolveCompactionModel(
      agent,
      targetModel.model,
      compactionConfig,
    );
    const result = await runLocalCompaction({
      agent,
      model,
      input: history,
      existingRecords: records,
      config: {
        ...compactionConfig,
        enabled: true,
      },
      trigger: options.trigger ?? 'manual',
      reason: 'manual',
      instructions: options.instructions,
      sessionId,
      sessionMemoryStore,
      signal: options.signal,
      force: true,
    });
    if (!result.didCompact || !result.boundaryItem || !result.summaryItem) {
      throw new UserError(
        'Unable to find a safe boundary for session compaction.',
      );
    }
    await store.appendRecords(sessionId, [
      result.boundaryItem,
      result.summaryItem,
    ]);
    return result;
  }

  private readonly inputGuardrailDefs: InputGuardrailDefinition[];
  private readonly outputGuardrailDefs: OutputGuardrailDefinition<
    OutputGuardrailMetadata,
    AgentOutputType<unknown>
  >[];

  async #runIndividualStream<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(
    agent: TAgent,
    input: string | AgentInputItem[] | RunState<TContext, TAgent>,
    options?: StreamRunOptions<TContext, TAgent>,
    ensureStreamInputPersisted?: () => Promise<void>,
    sessionInputUpdate?: (
      sourceItems: (AgentInputItem | undefined)[],
      filteredItems?: AgentInputItem[],
    ) => void,
    preserveTurnPersistenceOnResume?: boolean,
    sandboxMemoryRunContext?: SandboxMemoryPersistenceContext,
    localCompactionRuntime?: LocalCompactionRuntime,
    sessionTurnContext?: SessionTurnContext,
  ): Promise<StreamedRunResult<TContext, TAgent>> {
    options = options ?? ({} as StreamRunOptions<TContext>);
    return withNewSpanContext(async () => {
      // Initialize or reuse existing state
      const isResumedState = input instanceof RunState;
      const state: RunState<TContext, TAgent> = isResumedState
        ? input
        : new RunState(
            options.context instanceof RunContext
              ? options.context
              : new RunContext(options.context),
            input as string | AgentInputItem[],
            agent,
            options.maxTurns === undefined
              ? DEFAULT_MAX_TURNS
              : options.maxTurns,
          );
      if (isResumedState) {
        state._agentToolInvocation = undefined;
        if (options.maxTurns !== undefined) {
          state._maxTurns = options.maxTurns;
        }
      }
      const sandboxRuntime = new SandboxRuntimeManager<TContext>({
        startingAgent: agent,
        sandboxConfig: options.sandbox ?? this.config.sandbox,
        runState: isResumedState
          ? (state as RunState<TContext, Agent<TContext, AgentOutputType>>)
          : undefined,
      });
      const resolvedConversationId =
        options.conversationId ??
        (isResumedState ? state._conversationId : undefined);
      const resolvedPreviousResponseId =
        options.previousResponseId ??
        (isResumedState ? state._previousResponseId : undefined);
      if (!isResumedState) {
        state.setConversationContext(
          resolvedConversationId,
          resolvedPreviousResponseId,
        );
      }

      // Initialize the streamed result with existing state
      const result = new StreamedRunResult<TContext, TAgent>({
        signal: options.signal,
        state,
      });
      const streamOptions: StreamRunOptions<TContext, TAgent> = {
        ...options,
        signal: result._getAbortSignal(),
      };

      // Setup defaults
      result.maxTurns = state._maxTurns;

      // Continue the stream loop without blocking
      const streamLoopPromise = this.#runStreamLoop(
        result,
        agent,
        sandboxRuntime,
        streamOptions,
        isResumedState,
        ensureStreamInputPersisted,
        sessionInputUpdate,
        preserveTurnPersistenceOnResume,
        sandboxMemoryRunContext,
        localCompactionRuntime,
        sessionTurnContext,
      ).then(
        () => {
          result._done();
        },
        (err) => {
          result._raiseError(err);
        },
      );

      // Attach the stream loop promise so trace end waits for the loop to complete
      result._setStreamLoopPromise(streamLoopPromise);

      return result;
    });
  }

  async #runIndividualNonStream<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
    _THandoffs extends (Agent<any, any> | Handoff<any>)[] = any[],
  >(
    startingAgent: TAgent,
    input: string | AgentInputItem[] | RunState<TContext, TAgent>,
    options: NonStreamRunOptions<TContext, TAgent>,
    // sessionInputUpdate lets the caller adjust queued session items after filters run so we
    // persist exactly what we send to the model (e.g., after redactions or truncation).
    sessionInputUpdate?: (
      sourceItems: (AgentInputItem | undefined)[],
      filteredItems?: AgentInputItem[],
    ) => void,
    preserveTurnPersistenceOnResume?: boolean,
    sandboxMemoryRunContext?: SandboxMemoryPersistenceContext,
    localCompactionRuntime?: LocalCompactionRuntime,
  ): Promise<RunResult<TContext, TAgent>> {
    return withNewSpanContext(async () => {
      // if we have a saved state we use that one, otherwise we create a new one
      const isResumedState = input instanceof RunState;
      const state = isResumedState
        ? input
        : new RunState(
            options.context instanceof RunContext
              ? options.context
              : new RunContext(options.context),
            input,
            startingAgent,
            options.maxTurns === undefined
              ? DEFAULT_MAX_TURNS
              : options.maxTurns,
          );
      if (isResumedState) {
        state._agentToolInvocation = undefined;
        if (options.maxTurns !== undefined) {
          state._maxTurns = options.maxTurns;
        }
      }
      const sandboxRuntime = new SandboxRuntimeManager<TContext>({
        startingAgent,
        sandboxConfig: options.sandbox ?? this.config.sandbox,
        runState: isResumedState
          ? (state as RunState<TContext, Agent<TContext, AgentOutputType>>)
          : undefined,
      });
      const agentToolParentRunConfig =
        this.#getAgentToolParentRunConfig(options);
      const resolvedReasoningItemIdPolicy =
        options.reasoningItemIdPolicy ??
        (isResumedState ? state._reasoningItemIdPolicy : undefined) ??
        this.config.reasoningItemIdPolicy;
      state.setReasoningItemIdPolicy(resolvedReasoningItemIdPolicy);

      const resolvedConversationId =
        options.conversationId ??
        (isResumedState ? state._conversationId : undefined);
      const resolvedPreviousResponseId =
        options.previousResponseId ??
        (isResumedState ? state._previousResponseId : undefined);

      if (!isResumedState) {
        state.setConversationContext(
          resolvedConversationId,
          resolvedPreviousResponseId,
        );
      }

      const serverConversationTracker =
        resolvedConversationId || resolvedPreviousResponseId
          ? new ServerConversationTracker({
              conversationId: resolvedConversationId,
              previousResponseId: resolvedPreviousResponseId,
              reasoningItemIdPolicy: resolvedReasoningItemIdPolicy,
            })
          : undefined;

      if (serverConversationTracker && isResumedState) {
        serverConversationTracker.primeFromState({
          originalInput: state._originalInput,
          generatedItems: state._generatedItems,
          modelResponses: state._modelResponses,
        });
        state.setConversationContext(
          serverConversationTracker.conversationId,
          serverConversationTracker.previousResponseId,
        );
      }
      const toolErrorFormatter =
        options.toolErrorFormatter ?? this.config.toolErrorFormatter;

      // Tracks when we resume an approval interruption so the next run-again step stays in the same turn.
      let continuingInterruptedTurn = false;
      let runError: unknown;

      try {
        while (true) {
          // if we don't have a current step, we treat this as a new run
          state._currentStep = state._currentStep ?? {
            type: 'next_step_run_again',
          };

          if (state._currentStep.type === 'next_step_interruption') {
            await prepareSandboxInterruptedTurnResume({
              startingAgent,
              state,
              sandboxRuntime,
              runConfigModel: await this.#resolveSandboxRuntimeModelForAgent(
                state._currentAgent,
              ),
            });

            const interruptedOutcome = await resumeInterruptedTurn({
              state,
              runner: this,
              toolErrorFormatter,
              agentToolParentRunConfig,
            });

            // Don't reset counter here - resolveInterruptedTurn already adjusted it via rewind logic
            // The counter will be reset when _currentTurn is incremented (starting a new turn)

            const { shouldReturn, shouldContinue } = handleInterruptedOutcome({
              state,
              outcome: interruptedOutcome,
              setContinuingInterruptedTurn: (value) => {
                continuingInterruptedTurn = value;
              },
            });
            if (shouldReturn) {
              // we are still in an interruption, so we need to avoid an infinite loop
              return new RunResult<TContext, TAgent>(state);
            }
            if (shouldContinue) {
              continue;
            }
          }

          if (state._currentStep.type === 'next_step_run_again') {
            const wasContinuingInterruptedTurn = continuingInterruptedTurn;
            continuingInterruptedTurn = false;
            const guardrailTracker = createGuardrailTracker();
            const previousTurn = state._currentTurn;
            const previousPersistedCount = state._currentTurnPersistedItemCount;
            const previousGeneratedCount = state._generatedItems.length;
            const { turnInput, parallelGuardrailPromise } = await prepareTurn({
              state,
              input: state._originalInput,
              generatedItems: state._generatedItems,
              isResumedState,
              preserveTurnPersistenceOnResume,
              continuingInterruptedTurn: wasContinuingInterruptedTurn,
              serverConversationTracker,
              inputGuardrailDefs: this.inputGuardrailDefs,
              guardrailHandlers: {
                onParallelStart: guardrailTracker.markPending,
                onParallelError: guardrailTracker.setError,
              },
              emitAgentStart: (context, agent, inputItems) => {
                this.emit('agent_start', context, agent, inputItems);
              },
            });
            if (
              preserveTurnPersistenceOnResume &&
              state._currentTurn > previousTurn &&
              previousPersistedCount <= previousGeneratedCount
            ) {
              // Preserve persisted offsets from a resumed run to avoid re-saving prior items.
              state._currentTurnPersistedItemCount = previousPersistedCount;
            }

            guardrailTracker.setPromise(parallelGuardrailPromise);
            const preparedSandboxAgent = await sandboxRuntime.prepareAgent({
              currentAgent: state._currentAgent,
              turnInput,
              runConfigModel: await this.#resolveSandboxRuntimeModelForAgent(
                state._currentAgent,
              ),
            });
            const artifacts = await prepareAgentArtifacts(
              state,
              preparedSandboxAgent.executionAgent,
            );
            let preparedCall = await this.#prepareModelCall(
              state,
              preparedSandboxAgent.executionAgent,
              options,
              artifacts,
              preparedSandboxAgent.turnInput,
              serverConversationTracker,
              sessionInputUpdate,
            );

            if (localCompactionRuntime?.enabled) {
              await guardrailTracker.awaitCompletion();
            } else {
              guardrailTracker.throwIfError();
            }

            // Microcompact if necessary
            preparedCall = this.#maybeMicrocompactPreparedModelCall(
              preparedCall,
              localCompactionRuntime,
            );
            preparedCall = await this.#maybeCompactPreparedModelCall({
              state,
              agent: preparedSandboxAgent.executionAgent,
              options,
              preparedCall,
              localCompactionRuntime,
              streamResult: undefined,
            });

            const modelCallResult =
              await this.#getResponseWithReactiveCompaction({
                state,
                agent: preparedSandboxAgent.executionAgent,
                options,
                preparedCall,
                localCompactionRuntime,
              });
            preparedCall = modelCallResult.preparedCall;
            state._lastTurnResponse = modelCallResult.response;
            if (serverConversationTracker) {
              serverConversationTracker.markInputAsSent(
                preparedCall.sourceItems,
                {
                  filterApplied: preparedCall.filterApplied,
                  allTurnItems: preparedCall.turnInput,
                },
              );
            }
            state._modelResponses.push(state._lastTurnResponse);
            state._context.usage.add(state._lastTurnResponse.usage);
            state._noActiveAgentRun = false;

            // After each turn record the items echoed by the server so future requests only
            // include the incremental inputs that have not yet been acknowledged.
            serverConversationTracker?.trackServerItems(
              state._lastTurnResponse,
            );
            if (serverConversationTracker) {
              state.setConversationContext(
                serverConversationTracker.conversationId,
                serverConversationTracker.previousResponseId,
              );
            }

            const processedResponse = await processModelResponseAsync(
              state._lastTurnResponse,
              state._currentAgent,
              preparedCall.tools,
              preparedCall.handoffs,
              state,
              [...preparedCall.turnInput, ...state._generatedItems],
            );

            state._lastProcessedResponse = processedResponse;

            await guardrailTracker.awaitCompletion();

            const turnResult = await resolveTurnAfterModelResponse(
              state._currentAgent,
              state._originalInput,
              state._generatedItems,
              state._lastTurnResponse,
              state._lastProcessedResponse!,
              this,
              state,
              toolErrorFormatter,
              agentToolParentRunConfig,
              options.errorHandlers,
            );

            applyTurnResult({
              state,
              turnResult,
              agent: state._currentAgent,
              toolsUsed: state._lastProcessedResponse?.toolsUsed ?? [],
              resetTurnPersistence: !isResumedState,
            });
          }

          const currentStep = state._currentStep;
          if (!currentStep) {
            logger.debug('Running next loop');
            continue;
          }

          switch (currentStep.type) {
            case 'next_step_final_output':
              await runOutputGuardrails(
                state,
                this.outputGuardrailDefs,
                currentStep.output,
              );
              state._currentTurnInProgress = false;
              this.emit(
                'agent_end',
                state._context,
                state._currentAgent,
                currentStep.output,
              );
              state._currentAgent.emit(
                'agent_end',
                state._context,
                currentStep.output,
              );
              return new RunResult<TContext, TAgent>(state);
            case 'next_step_handoff':
              state.setCurrentAgent(currentStep.newAgent as TAgent);
              if (state._currentAgentSpan) {
                state._currentAgentSpan.end();
                resetCurrentSpan();
                state.setCurrentAgentSpan(undefined);
              }
              state._noActiveAgentRun = true;
              state._currentTurnInProgress = false;

              // We've processed the handoff, so we need to run the loop again.
              state._currentStep = { type: 'next_step_run_again' };
              break;
            case 'next_step_interruption':
              // Interrupted. Don't run any guardrails.
              return new RunResult<TContext, TAgent>(state);
            case 'next_step_run_again':
              state._currentTurnInProgress = false;
              logger.debug('Running next loop');
              break;
            default:
              logger.debug('Running next loop');
          }
        }
      } catch (err) {
        state._currentTurnInProgress = false;
        const handledResult = await tryHandleRunError({
          error: err,
          state,
          errorHandlers: options.errorHandlers,
          outputGuardrailDefs: this.outputGuardrailDefs,
          emitAgentEnd: (context, agent, outputText) => {
            this.emit('agent_end', context, agent, outputText);
            agent.emit('agent_end', context, outputText);
          },
        });
        if (handledResult) {
          return handledResult;
        }
        if (state._currentAgentSpan) {
          state._currentAgentSpan.setError({
            message: 'Error in agent run',
            data: { error: String(err) },
          });
        }
        runError = err;
        throw err;
      } finally {
        const preserveSandboxSessions =
          state._currentStep?.type === 'next_step_interruption';
        await finalizeSandboxRuntime({
          state: state as RunState<TContext, Agent<TContext, AgentOutputType>>,
          sandboxRuntime,
          preserveSessionsForInterruption: preserveSandboxSessions,
          runError,
          groupId: this.config.groupId,
          memoryContext: sandboxMemoryRunContext,
          runAgent: async (agent, input, runOptions) =>
            await this.run(agent, input, runOptions),
        });
      }
    });
  }

  async #runStreamLoop<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(
    result: StreamedRunResult<TContext, TAgent>,
    startingAgent: TAgent,
    sandboxRuntime: SandboxRuntimeManager<TContext>,
    options: StreamRunOptions<TContext, TAgent>,
    isResumedState: boolean,
    ensureStreamInputPersisted?: () => Promise<void>,
    sessionInputUpdate?: (
      sourceItems: (AgentInputItem | undefined)[],
      filteredItems?: AgentInputItem[],
    ) => void,
    preserveTurnPersistenceOnResume?: boolean,
    sandboxMemoryRunContext?: SandboxMemoryPersistenceContext,
    localCompactionRuntime?: LocalCompactionRuntime,
    sessionTurnContext?: SessionTurnContext,
  ): Promise<void> {
    const resolvedReasoningItemIdPolicy =
      options.reasoningItemIdPolicy ??
      (isResumedState ? result.state._reasoningItemIdPolicy : undefined) ??
      this.config.reasoningItemIdPolicy;
    result.state.setReasoningItemIdPolicy(resolvedReasoningItemIdPolicy);
    const resolvedConversationId =
      options.conversationId ?? result.state._conversationId;
    const resolvedPreviousResponseId =
      options.previousResponseId ?? result.state._previousResponseId;
    const serverManagesConversation =
      Boolean(resolvedConversationId) || Boolean(resolvedPreviousResponseId);
    const serverConversationTracker = serverManagesConversation
      ? new ServerConversationTracker({
          conversationId: resolvedConversationId,
          previousResponseId: resolvedPreviousResponseId,
          reasoningItemIdPolicy: resolvedReasoningItemIdPolicy,
        })
      : undefined;
    if (serverConversationTracker) {
      result.state.setConversationContext(
        serverConversationTracker.conversationId,
        serverConversationTracker.previousResponseId,
      );
    }

    let sentInputToModel = false;
    let streamInputPersisted = false;
    let guardrailTracker = createGuardrailTracker();
    const persistStreamInputIfNeeded = async () => {
      if (streamInputPersisted || !ensureStreamInputPersisted) {
        return;
      }
      // Both success and error paths call this helper, so guard against multiple writes.
      await ensureStreamInputPersisted();
      streamInputPersisted = true;
    };
    let parallelGuardrailPromise: Promise<InputGuardrailResult[]> | undefined;
    const awaitGuardrailsAndPersistInput = async () => {
      await guardrailTracker.awaitCompletion();
      if (guardrailTracker.failed) {
        throw guardrailTracker.error;
      }
      if (
        sentInputToModel &&
        !streamInputPersisted &&
        !guardrailTracker.failed
      ) {
        await persistStreamInputIfNeeded();
      }
    };

    if (serverConversationTracker && isResumedState) {
      serverConversationTracker.primeFromState({
        originalInput: result.state._originalInput,
        generatedItems: result.state._generatedItems,
        modelResponses: result.state._modelResponses,
      });
      result.state.setConversationContext(
        serverConversationTracker.conversationId,
        serverConversationTracker.previousResponseId,
      );
    }
    const toolErrorFormatter =
      options.toolErrorFormatter ?? this.config.toolErrorFormatter;
    const agentToolParentRunConfig = this.#getAgentToolParentRunConfig(options);

    // Tracks when we resume an approval interruption so the next run-again step stays in the same turn.
    let continuingInterruptedTurn = false;
    let runError: unknown;

    try {
      while (true) {
        const currentAgent = result.state._currentAgent;

        result.state._currentStep = result.state._currentStep ?? {
          type: 'next_step_run_again',
        };

        if (result.state._currentStep.type === 'next_step_interruption') {
          await prepareSandboxInterruptedTurnResume({
            startingAgent,
            state: result.state,
            sandboxRuntime,
            runConfigModel: await this.#resolveSandboxRuntimeModelForAgent(
              result.state._currentAgent,
            ),
          });

          const interruptedOutcome = await resumeInterruptedTurn({
            state: result.state,
            runner: this,
            toolErrorFormatter,
            agentToolParentRunConfig,
            onStepItems: (turnResult) => {
              addStepToRunResult(result, turnResult);
            },
          });

          // Don't reset counter here - resolveInterruptedTurn already adjusted it via rewind logic
          // The counter will be reset when _currentTurn is incremented (starting a new turn)

          const { shouldReturn, shouldContinue } = handleInterruptedOutcome({
            state: result.state,
            outcome: interruptedOutcome,
            setContinuingInterruptedTurn: (value) => {
              continuingInterruptedTurn = value;
            },
          });
          if (shouldReturn) {
            // we are still in an interruption, so we need to avoid an infinite loop
            return;
          }
          if (shouldContinue) {
            continue;
          }
        }

        if (result.state._currentStep.type === 'next_step_run_again') {
          parallelGuardrailPromise = undefined;
          guardrailTracker = createGuardrailTracker();
          const wasContinuingInterruptedTurn = continuingInterruptedTurn;
          continuingInterruptedTurn = false;
          const previousTurn = result.state._currentTurn;
          const previousPersistedCount =
            result.state._currentTurnPersistedItemCount;
          const previousGeneratedCount = result.state._generatedItems.length;
          const preparedTurn = await prepareTurn({
            state: result.state,
            input: result.input,
            generatedItems: result.newItems,
            isResumedState,
            preserveTurnPersistenceOnResume,
            continuingInterruptedTurn: wasContinuingInterruptedTurn,
            serverConversationTracker,
            inputGuardrailDefs: this.inputGuardrailDefs,
            guardrailHandlers: {
              onParallelStart: () => {
                guardrailTracker.markPending();
              },
              onParallelError: (err) => {
                guardrailTracker.setError(err);
              },
            },
            emitAgentStart: (context, agent, inputItems) => {
              this.emit('agent_start', context, agent, inputItems);
            },
          });
          if (
            preserveTurnPersistenceOnResume &&
            result.state._currentTurn > previousTurn &&
            previousPersistedCount <= previousGeneratedCount
          ) {
            // Preserve persisted offsets from a resumed run to avoid re-saving prior items.
            result.state._currentTurnPersistedItemCount =
              previousPersistedCount;
          }
          const { turnInput } = preparedTurn;
          parallelGuardrailPromise = preparedTurn.parallelGuardrailPromise;
          guardrailTracker.setPromise(parallelGuardrailPromise);
          // If guardrails are still running, defer input persistence until they finish.
          const delayStreamInputPersistence = guardrailTracker.pending;
          const preparedSandboxAgent = await sandboxRuntime.prepareAgent({
            currentAgent: result.state._currentAgent,
            turnInput,
            runConfigModel: await this.#resolveSandboxRuntimeModelForAgent(
              result.state._currentAgent,
            ),
          });
          const artifacts = await prepareAgentArtifacts(
            result.state,
            preparedSandboxAgent.executionAgent,
          );

          let preparedCall = await this.#prepareModelCall(
            result.state,
            preparedSandboxAgent.executionAgent,
            options,
            artifacts,
            preparedSandboxAgent.turnInput,
            serverConversationTracker,
            sessionInputUpdate,
          );

          if (localCompactionRuntime?.enabled) {
            await guardrailTracker.awaitCompletion();
          } else {
            guardrailTracker.throwIfError();
          }

          preparedCall = this.#maybeMicrocompactPreparedModelCall(
            preparedCall,
            localCompactionRuntime,
          );
          preparedCall = await this.#maybeCompactPreparedModelCall({
            state: result.state,
            agent: preparedSandboxAgent.executionAgent,
            options,
            preparedCall,
            localCompactionRuntime,
            streamResult: result,
          });

          let finalResponse: ModelResponse | undefined;
          const partialStreamEvents: ResponseStreamEvent[] = [];
          const abortReconciliationState =
            createStreamAbortReconciliationState();
          let inputMarked = false;
          const markInputOnce = () => {
            if (inputMarked || !serverConversationTracker) {
              return;
            }
            // We only mark inputs as sent after receiving the first stream event,
            // which is the earliest reliable confirmation that the server accepted
            // the request. If the stream fails before any events, leave inputs
            // unmarked so a retry can resend safely.
            // Record the exact input that was sent so the server tracker can advance safely.
            serverConversationTracker.markInputAsSent(
              preparedCall.sourceItems,
              {
                filterApplied: preparedCall.filterApplied,
                allTurnItems: preparedCall.turnInput,
              },
            );
            inputMarked = true;
          };
          const reconcileStreamAbortIfNeeded = async () => {
            if (
              !serverConversationTracker ||
              !shouldReconcileStreamAbort(abortReconciliationState)
            ) {
              return;
            }

            const reconciliationInput = buildAbortReconciliationInput(
              abortReconciliationState,
            );
            try {
              const reconciliationResponse = await getResponseWithRetry(
                preparedCall.model,
                {
                  systemInstructions: preparedCall.modelInput.instructions,
                  // prompt: preparedCall.prompt,
                  ...(preparedCall.explictlyModelSet
                    ? { overridePromptModel: true }
                    : {}),
                  input: reconciliationInput,
                  previousResponseId: getAbortReconciliationPreviousResponseId(
                    abortReconciliationState,
                    preparedCall,
                  ),
                  conversationId: preparedCall.conversationId,
                  modelSettings: preparedCall.modelSettings,
                  tools: preparedCall.serializedTools,
                  toolsExplicitlyProvided: preparedCall.toolsExplicitlyProvided,
                  handoffs: preparedCall.serializedHandoffs,
                  outputType: convertAgentOutputTypeToSerializable(
                    currentAgent.outputType,
                  ),
                  tracing: getTracing(
                    this.config.tracingDisabled,
                    this.config.traceIncludeSensitiveData,
                  ),
                },
              );
              markAbortReconciliationComplete(
                abortReconciliationState,
                reconciliationResponse,
              );
              serverConversationTracker.trackServerItems(
                reconciliationResponse,
              );
              result.state.setConversationContext(
                serverConversationTracker.conversationId,
                serverConversationTracker.previousResponseId,
              );
            } catch (error) {
              logger.debug(
                'Failed to reconcile streamed function calls after abort.',
                error,
              );
            }
          };
          const finalizePartialExternalAbortIfNeeded = async () => {
            if (!result._isExternalSignalAborted()) {
              return;
            }

            // The aborted turn may have either fully streamed its final response
            // (`finalResponse` is set, but normal post-stream processing was skipped because the
            // cancel short-circuits the loop) or been cut off mid-stream. In both cases the
            // produced items have not been committed yet, so commit them here — otherwise
            // already-streamed content (e.g. text emitted in the same model turn as a tool call)
            // is lost on reload even though the prior, separately-committed turns survive.
            //
            // Only the assistant text/reasoning is committed; any tool call in the final response
            // is stripped. The call never executed (so it is pruned anyway), and routing it through
            // tool resolution here can throw on sandbox/unknown tools — which would roll back and
            // sink the entire persist, losing even the earlier, already-streamed turns.
            const partial = finalResponse
              ? undefined
              : buildPartialAbortModelResponse(partialStreamEvents);
            const responseToProcess: ModelResponse | undefined = finalResponse
              ? {
                  ...finalResponse,
                  output: finalResponse.output.filter(
                    (item) =>
                      item.type === 'message' || item.type === 'reasoning',
                  ),
                }
              : partial?.response;
            const previousGeneratedItems = result.state._generatedItems;
            const previousModelResponses = result.state._modelResponses;
            const previousLastTurnResponse = result.state._lastTurnResponse;
            const previousLastProcessedResponse =
              result.state._lastProcessedResponse;
            const previousPersistedItemCount =
              result.state._currentTurnPersistedItemCount;

            let committedNewItems: RunItem[] = [];
            if (responseToProcess) {
              try {
                const processedResponse = await processModelResponseAsync(
                  responseToProcess,
                  currentAgent,
                  preparedCall.tools,
                  preparedCall.handoffs,
                  result.state,
                  [...preparedCall.turnInput, ...result.state._generatedItems],
                );

                if (processedResponse.newItems.length > 0) {
                  committedNewItems = processedResponse.newItems;
                  result.state._lastTurnResponse = responseToProcess;
                  result.state._lastProcessedResponse = processedResponse;
                  result.state._modelResponses = [
                    ...result.state._modelResponses,
                    responseToProcess,
                  ];
                  result.state._generatedItems = [
                    ...result.state._generatedItems,
                    ...processedResponse.newItems,
                  ];
                }
              } catch (error) {
                // Defensive: a malformed aborted response must never sink the whole turn — fall
                // back to persisting items already committed by earlier model calls in this turn.
                logger.debug(
                  `Failed to process aborted final response; persisting already-committed items only: ${error}`,
                );
              }
            }

            const abortPersistableGeneratedItems =
              pruneIncompleteToolCallsForExternalAbort(
                result.state._generatedItems,
                result.state._currentTurnPersistedItemCount,
              );
            const hasUnpersistedGeneratedItems =
              abortPersistableGeneratedItems.length >
              result.state._currentTurnPersistedItemCount;

            try {
              const outputTextForGuardrails =
                partial?.outputText ?? extractAllTextOutput(committedNewItems);
              if (outputTextForGuardrails) {
                await runOutputGuardrails(
                  result.state,
                  this.outputGuardrailDefs,
                  outputTextForGuardrails,
                );
              }
              result.state._currentTurnInProgress = false;
              await persistStreamInputIfNeeded();
              if (!serverManagesConversation && hasUnpersistedGeneratedItems) {
                result.state._generatedItems = abortPersistableGeneratedItems;
                await saveStreamResultToSession(options.session, result, {
                  compactionStore: localCompactionRuntime?.store,
                  compactionSessionId: localCompactionRuntime?.sessionId,
                  turnContext: sessionTurnContext,
                });
              }
            } catch (error) {
              result.state._generatedItems = previousGeneratedItems;
              result.state._modelResponses = previousModelResponses;
              result.state._lastTurnResponse = previousLastTurnResponse;
              result.state._lastProcessedResponse =
                previousLastProcessedResponse;
              result.state._currentTurnPersistedItemCount =
                previousPersistedItemCount;
              throw error;
            }
          };

          sentInputToModel = true;
          const markInputOnStreamAbort = () => {
            if (sentInputToModel) {
              markInputOnce();
            }
          };
          let removeMarkInputOnStreamAbort = () => {};
          if (serverConversationTracker && options.signal) {
            if (options.signal.aborted) {
              markInputOnStreamAbort();
            } else {
              options.signal.addEventListener('abort', markInputOnStreamAbort, {
                once: true,
              });
              removeMarkInputOnStreamAbort = () => {
                options.signal?.removeEventListener(
                  'abort',
                  markInputOnStreamAbort,
                );
              };
            }
          }
          if (!delayStreamInputPersistence) {
            await persistStreamInputIfNeeded();
          }

          let reactiveStreamRetryAttempted = false;
          try {
            while (true) {
              try {
                for await (const event of getStreamedResponseWithRetry(
                  preparedCall.model,
                  {
                    systemInstructions: preparedCall.modelInput.instructions,
                    // prompt: preparedCall.prompt,
                    // Streaming requests should also honor explicitly chosen models.
                    ...(preparedCall.explictlyModelSet
                      ? { overridePromptModel: true }
                      : {}),
                    input: preparedCall.modelInput.input,
                    previousResponseId: preparedCall.previousResponseId,
                    conversationId: preparedCall.conversationId,
                    modelSettings: preparedCall.modelSettings,
                    tools: preparedCall.serializedTools,
                    toolsExplicitlyProvided:
                      preparedCall.toolsExplicitlyProvided,
                    handoffs: preparedCall.serializedHandoffs,
                    outputType: convertAgentOutputTypeToSerializable(
                      currentAgent.outputType,
                    ),
                    tracing: getTracing(
                      this.config.tracingDisabled,
                      this.config.traceIncludeSensitiveData,
                    ),
                    signal: options.signal,
                  },
                )) {
                  guardrailTracker.throwIfError();
                  markInputOnce();
                  partialStreamEvents.push(event);
                  recordStreamEventForAbortReconciliation(
                    abortReconciliationState,
                    event,
                  );
                  if (event.type === 'response_done') {
                    const parsed = StreamEventResponseCompleted.parse(event);
                    finalResponse = {
                      usage: new Usage(parsed.response.usage),
                      output: parsed.response.output,
                      responseId: parsed.response.id,
                      requestId: parsed.response.requestId,
                    };
                    result.state._context.usage.add(finalResponse.usage);
                  }
                  if (result.cancelled) {
                    // When the user's code exits a loop to consume the stream, we need to break
                    // this loop to prevent internal false errors and unnecessary processing
                    await awaitGuardrailsAndPersistInput();
                    await finalizePartialExternalAbortIfNeeded();
                    await reconcileStreamAbortIfNeeded();
                    return;
                  }
                  result._addItem(new RunRawModelStreamEvent(event));
                }
                break;
              } catch (error) {
                if (isAbortError(error)) {
                  if (sentInputToModel) {
                    markInputOnce();
                  }
                  await awaitGuardrailsAndPersistInput();
                  await finalizePartialExternalAbortIfNeeded();
                  await reconcileStreamAbortIfNeeded();
                  return;
                }
                if (
                  !reactiveStreamRetryAttempted &&
                  !inputMarked &&
                  isContextLengthExceededError(error) &&
                  localCompactionRuntime?.enabled
                ) {
                  const compactedCall =
                    await this.#maybeCompactPreparedModelCall({
                      state: result.state,
                      agent: preparedSandboxAgent.executionAgent,
                      options,
                      preparedCall,
                      localCompactionRuntime,
                      streamResult: result,
                      triggerOverride: 'reactive',
                      force: true,
                    });
                  reactiveStreamRetryAttempted = true;
                  if (compactedCall !== preparedCall) {
                    preparedCall = compactedCall;
                    continue;
                  }
                }
                throw error;
              }
            }
          } finally {
            removeMarkInputOnStreamAbort();
          }

          if (finalResponse) {
            markInputOnce();
          }

          await awaitGuardrailsAndPersistInput();

          if (result.cancelled) {
            await finalizePartialExternalAbortIfNeeded();
            await reconcileStreamAbortIfNeeded();
            return;
          }

          result.state._noActiveAgentRun = false;

          if (!finalResponse) {
            throw new ModelBehaviorError(
              'Model did not produce a final response!',
              result.state,
            );
          }

          result.state._lastTurnResponse = finalResponse;
          // Keep the tracker in sync with the streamed response so reconnections remain accurate.
          serverConversationTracker?.trackServerItems(finalResponse);
          if (serverConversationTracker) {
            result.state.setConversationContext(
              serverConversationTracker.conversationId,
              serverConversationTracker.previousResponseId,
            );
          }
          result.state._modelResponses.push(result.state._lastTurnResponse);
          const processedResponse = await processModelResponseAsync(
            result.state._lastTurnResponse,
            currentAgent,
            preparedCall.tools,
            preparedCall.handoffs,
            result.state,
            [...preparedCall.turnInput, ...result.state._generatedItems],
          );

          result.state._lastProcessedResponse = processedResponse;

          // Record the items emitted directly from the model response so we do not
          // stream them again after tools and other side effects finish.
          const preToolItems = new Set<RunItem>(processedResponse.newItems);
          if (preToolItems.size > 0) {
            streamStepItemsToRunResult(result, processedResponse.newItems);
          }

          const turnResult = await resolveTurnAfterModelResponse(
            currentAgent,
            result.state._originalInput,
            result.state._generatedItems,
            result.state._lastTurnResponse,
            result.state._lastProcessedResponse!,
            this,
            result.state,
            toolErrorFormatter,
            agentToolParentRunConfig,
            options.errorHandlers,
          );

          applyTurnResult({
            state: result.state,
            turnResult,
            agent: currentAgent,
            toolsUsed: processedResponse.toolsUsed,
            resetTurnPersistence: !isResumedState,
            onStepItems: (step) => {
              addStepToRunResult(result, step, { skipItems: preToolItems });
            },
          });
        }

        const currentStep = result.state._currentStep;
        switch (currentStep.type) {
          case 'next_step_final_output':
            try {
              await runOutputGuardrails(
                result.state,
                this.outputGuardrailDefs,
                currentStep.output,
              );
            } catch (error) {
              // Do not leave blocked output visible through StreamedRunResult.finalOutput.
              result.state._currentStep = undefined;
              result.state._finalOutputSource = undefined;
              throw error;
            }
            result.state._currentTurnInProgress = false;
            await persistStreamInputIfNeeded();
            // Guardrails must succeed before persisting session memory to avoid storing blocked outputs.
            if (!serverManagesConversation) {
              await saveStreamResultToSession(options.session, result, {
                compactionStore: localCompactionRuntime?.store,
                compactionSessionId: localCompactionRuntime?.sessionId,
                turnContext: sessionTurnContext,
              });
              this.#scheduleSessionMemoryUpdate({
                agent: result.state._currentAgent,
                session: options.session,
                localCompactionRuntime,
              });
            }
            this.emit(
              'agent_end',
              result.state._context,
              currentAgent,
              currentStep.output,
            );
            currentAgent.emit(
              'agent_end',
              result.state._context,
              currentStep.output,
            );
            return;
          case 'next_step_interruption':
            // We are done for now. Don't run any output guardrails.
            await persistStreamInputIfNeeded();
            if (!serverManagesConversation) {
              await saveStreamResultToSession(options.session, result, {
                compactionStore: localCompactionRuntime?.store,
                compactionSessionId: localCompactionRuntime?.sessionId,
                turnContext: sessionTurnContext,
              });
              this.#scheduleSessionMemoryUpdate({
                agent: result.state._currentAgent,
                session: options.session,
                localCompactionRuntime,
              });
            }
            return;
          case 'next_step_handoff':
            result.state.setCurrentAgent(currentStep.newAgent as TAgent);
            if (result.state._currentAgentSpan) {
              result.state._currentAgentSpan.end();
              resetCurrentSpan();
            }
            result.state.setCurrentAgentSpan(undefined);
            result._addItem(
              new RunAgentUpdatedStreamEvent(result.state._currentAgent),
            );
            result.state._noActiveAgentRun = true;
            result.state._currentTurnInProgress = false;

            // We've processed the handoff, so we need to run the loop again.
            result.state._currentStep = {
              type: 'next_step_run_again',
            };
            break;
          case 'next_step_run_again':
            result.state._currentTurnInProgress = false;
            logger.debug('Running next loop');
            break;
          default:
            logger.debug('Running next loop');
        }
      }
    } catch (error) {
      result.state._currentTurnInProgress = false;
      if (guardrailTracker.pending) {
        await guardrailTracker.awaitCompletion({ suppressErrors: true });
      }
      if (
        sentInputToModel &&
        !streamInputPersisted &&
        !guardrailTracker.failed
      ) {
        await persistStreamInputIfNeeded();
      }
      const handledResult = await tryHandleRunError({
        error,
        state: result.state,
        errorHandlers: options.errorHandlers,
        outputGuardrailDefs: this.outputGuardrailDefs,
        emitAgentEnd: (context, agent, outputText) => {
          this.emit('agent_end', context, agent, outputText);
          agent.emit('agent_end', context, outputText);
        },
        streamResult: result,
      });
      if (handledResult) {
        await persistStreamInputIfNeeded();
        if (!serverManagesConversation) {
          await saveStreamResultToSession(options.session, result, {
            compactionStore: localCompactionRuntime?.store,
            compactionSessionId: localCompactionRuntime?.sessionId,
            turnContext: sessionTurnContext,
          });
          this.#scheduleSessionMemoryUpdate({
            agent: result.state._currentAgent,
            session: options.session,
            localCompactionRuntime,
          });
        }
        return;
      }
      // Preserve whatever this turn already produced (e.g. earlier successful
      // tool calls, streamed text) before propagating the error. Otherwise the
      // whole turn is dropped on reload, leaving an empty turn with no record of
      // what happened — the original "blank assistant bubble, no error" symptom.
      // Content blocked by a guardrail must never be stored, so skip persistence
      // when the failure is a guardrail tripwire.
      const isGuardrailBlock =
        guardrailTracker.failed ||
        error instanceof OutputGuardrailTripwireTriggered ||
        error instanceof InputGuardrailTripwireTriggered;
      if (!serverManagesConversation && !isGuardrailBlock) {
        const persistableGeneratedItems =
          pruneIncompleteToolCallsForExternalAbort(
            result.state._generatedItems,
            result.state._currentTurnPersistedItemCount,
          );
        const hasUnpersistedGeneratedItems =
          persistableGeneratedItems.length >
          result.state._currentTurnPersistedItemCount;
        if (hasUnpersistedGeneratedItems) {
          try {
            result.state._generatedItems = persistableGeneratedItems;
            await saveStreamResultToSession(options.session, result, {
              compactionStore: localCompactionRuntime?.store,
              compactionSessionId: localCompactionRuntime?.sessionId,
              turnContext: sessionTurnContext,
            });
          } catch (persistError) {
            // Best-effort: never mask the original run error with a persist failure.
            logger.debug(
              `Failed to persist partial turn after run error: ${persistError}`,
            );
          }
        }
      }
      if (result.state._currentAgentSpan) {
        result.state._currentAgentSpan.setError({
          message: 'Error in agent run',
          data: { error: String(error) },
        });
      }
      runError = error;
      throw error;
    } finally {
      if (guardrailTracker.pending) {
        await guardrailTracker.awaitCompletion({ suppressErrors: true });
      }
      if (
        sentInputToModel &&
        !streamInputPersisted &&
        !guardrailTracker.failed
      ) {
        await persistStreamInputIfNeeded();
      }
      const preserveSandboxSessions =
        result.state._currentStep?.type === 'next_step_interruption';
      await finalizeSandboxRuntime({
        state: result.state as RunState<
          TContext,
          Agent<TContext, AgentOutputType>
        >,
        sandboxRuntime,
        preserveSessionsForInterruption: preserveSandboxSessions,
        runError,
        groupId: this.config.groupId,
        memoryContext: sandboxMemoryRunContext,
        runAgent: async (agent, input, runOptions) =>
          await this.run(agent, input, runOptions),
      });
    }
  }

  async #maybeCompactPreparedModelCall<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(options: {
    state: RunState<TContext, TAgent>;
    agent: Agent<TContext, AgentOutputType>;
    options: SharedRunOptions<TContext, TAgent>;
    preparedCall: PreparedModelCall<TContext>;
    localCompactionRuntime?: LocalCompactionRuntime;
    streamResult?: StreamedRunResult<TContext, TAgent>;
    triggerOverride?: CompactTrigger;
    force?: boolean;
  }): Promise<PreparedModelCall<TContext>> {
    const {
      state,
      agent,
      options: runOptions,
      preparedCall, // 已经准备好的模型调用。
      localCompactionRuntime,
      streamResult,
      triggerOverride,
    } = options;
    if (!localCompactionRuntime?.enabled) {
      return preparedCall;
    }
    if (preparedCall.conversationId || preparedCall.previousResponseId) {
      // in fact, there is no way to passthrough here.
      return preparedCall;
    }

    const manualCompact =
      typeof runOptions.compact === 'object' && runOptions.compact?.force;
    const trigger: CompactTrigger =
      triggerOverride ?? (manualCompact ? 'manual' : 'auto');
    const reason =
      trigger === 'reactive'
        ? 'context_length_exceeded'
        : trigger === 'manual'
          ? 'manual'
          : 'threshold';
    if (trigger === 'auto' && !localCompactionRuntime.config.auto) {
      return preparedCall; // 不执行压缩，返回原始模型调用。
    }
    const fuseKey = localCompactionRuntime.sessionId ?? '__ephemeral__';
    if (
      // 判断自动压缩是否已经因为连续失败触发熔断。
      trigger === 'auto' &&
      (this.localCompactionFailures.get(fuseKey) ?? 0) >=
        localCompactionRuntime.config.failureFuseThreshold
    ) {
      return preparedCall;
    }

    // 将模型 metadata 中的上下文窗口信息合并到压缩配置里。
    const compactionConfig = applyModelMetadataToCompactionConfig(
      localCompactionRuntime.config,
      preparedCall.modelMetadata,
    );

    const thresholdTokens = calculateCompactionThreshold(compactionConfig);
    const estimatedPreTokens = estimateTokensForItems(
      // 估算当前模型输入在压缩前的 token 数。
      preparedCall.modelInput.input,
    );

    let startedEventId: string | undefined;
    try {
      // 判断是否需要等待 session memory 后台更新完成。
      if (
        options.force ||
        trigger !== 'auto' ||
        estimatedPreTokens >= thresholdTokens
      ) {
        await this.#waitForSessionMemoryUpdate(localCompactionRuntime);
      }
      const model = await this.#resolveCompactionModel(
        agent,
        preparedCall.model,
        compactionConfig,
      );

      const result = await runLocalCompaction({
        agent,
        model,
        input: preparedCall.modelInput.input,
        existingRecords: localCompactionRuntime.records,
        config: compactionConfig,
        trigger,
        reason,
        instructions:
          typeof runOptions.compact === 'object'
            ? runOptions.compact.instructions
            : undefined,
        sessionId: localCompactionRuntime.sessionId,
        sessionMemoryStore: localCompactionRuntime.sessionMemoryStore,
        signal: runOptions.signal,
        force: options.force ?? Boolean(manualCompact),
        onStarted: (data) => {
          startedEventId = data.id;
          streamResult?._addItem(
            // 如果是 streaming 运行，则向结果流写入 compact_started 事件。
            new RunCompactionStreamEvent('compact_started', {
              id: data.id,
              trigger,
              source: data.source,
              reason,
              thresholdTokens,
              preCompactTokenCount: data.preCompactTokenCount,
              summarizedItemCount: data.summarizedItemCount,
              preservedItemCount: data.preservedItemCount,
            }),
          );
        },
      });
      if (!result.didCompact || !result.modelInput) {
        return preparedCall;
      }

      this.localCompactionFailures.delete(fuseKey);
      state._context.usage.add(result.usage);
      state._generatedItems.push(...result.runItems);
      if (result.boundaryItem && result.summaryItem) {
        localCompactionRuntime.records.push(
          result.boundaryItem,
          result.summaryItem,
        );
      }
      if (result.boundaryItem && result.summaryItem) {
        state._activeLocalCompaction = {
          boundaryId: result.boundaryItem.id, // 记录当前 active boundary id。
          summaryItemId: result.summaryItem.id, // 记录当前 active summary id。
          baseModelContext: result.modelInput, // 保存压缩后的基础模型上下文。
          generatedItemStartIndex: state._generatedItems.length, // 标记压缩完成后新增生成项的起始位置。
        };
      }
      streamResult?._addItem(
        // 如果是 streaming 运行，则写入 compact_completed 事件。
        new RunCompactionStreamEvent('compact_completed', {
          id: result.id ?? startedEventId ?? '',
          trigger,
          source: result.source,
          reason,
          thresholdTokens,
          preCompactTokenCount: result.preCompactTokenCount,
          postCompactTokenCount: result.postCompactTokenCount,
          summarizedItemCount: result.summarizedItemCount,
          preservedItemCount: result.preservedItemCount,
          boundaryItem: result.boundaryItem,
          summaryItem: result.summaryItem,
        }),
      );
      if (streamResult) {
        streamStepItemsToRunResult(streamResult, result.runItems);
      }

      return {
        ...preparedCall,
        modelInput: {
          ...preparedCall.modelInput,
          input: result.modelInput,
        },
        sourceItems: result.modelInput,
        turnInput: result.modelInput,
      };
    } catch (error) {
      this.localCompactionFailures.set(
        fuseKey,
        (this.localCompactionFailures.get(fuseKey) ?? 0) + 1, // 将失败计数加一。
      );
      streamResult?._addItem(
        new RunCompactionStreamEvent('compact_failed', {
          id: startedEventId ?? '',
          trigger,
          reason,
          thresholdTokens,
          error: error instanceof Error ? error.message : String(error),
        }),
      );
      if (trigger === 'manual') {
        // 如果是手动压缩，失败应该反馈给调用方。
        throw error;
      }
      return preparedCall;
    }
  }

  #maybeMicrocompactPreparedModelCall<TContext>(
    preparedCall: PreparedModelCall<TContext>,
    localCompactionRuntime?: LocalCompactionRuntime,
  ): PreparedModelCall<TContext> {
    if (!localCompactionRuntime?.enabled) {
      return preparedCall;
    }
    const input = microcompactModelContext(
      preparedCall.modelInput.input,
      localCompactionRuntime.config.microcompact,
    );
    if (input === preparedCall.modelInput.input) {
      return preparedCall;
    }
    return {
      ...preparedCall,
      modelInput: {
        ...preparedCall.modelInput,
        input,
      },
    };
  }

  #scheduleSessionMemoryUpdate<TContext>(options: {
    agent: Agent<TContext, AgentOutputType>;
    session?: Session;
    localCompactionRuntime?: LocalCompactionRuntime;
  }): void {
    const { session, localCompactionRuntime } = options;
    if (
      !session ||
      !localCompactionRuntime?.enabled ||
      !localCompactionRuntime.sessionId ||
      !localCompactionRuntime.sessionMemoryStore ||
      !localCompactionRuntime.sessionMemoryUpdateConfig
    ) {
      return;
    }
    const sessionId = localCompactionRuntime.sessionId;
    const store = localCompactionRuntime.sessionMemoryStore;
    const config = localCompactionRuntime.sessionMemoryUpdateConfig;
    this.sessionMemoryUpdateQueue.schedule({
      sessionId,
      run: async () => {
        const items = await session.getItems();
        const model = await this.#resolveSessionMemoryUpdateModel(
          options.agent,
          config,
        );
        await updateSessionMemory({
          model,
          store,
          sessionId,
          items,
          config,
        });
      },
    });
  }

  async #waitForSessionMemoryUpdate(
    localCompactionRuntime?: LocalCompactionRuntime,
  ): Promise<void> {
    if (
      !localCompactionRuntime?.sessionId ||
      !localCompactionRuntime.sessionMemoryStore ||
      !localCompactionRuntime.sessionMemoryUpdateConfig
    ) {
      return;
    }
    await this.sessionMemoryUpdateQueue.waitForIdle(
      localCompactionRuntime.sessionId,
      localCompactionRuntime.sessionMemoryUpdateConfig.waitForUpdateMs,
    );
  }

  async #resolveSessionMemoryUpdateModel<TContext>(
    agent: Agent<TContext, AgentOutputType>,
    config: ResolvedSessionMemoryBackgroundUpdateConfig,
  ): Promise<Model> {
    if (config.model && typeof config.model !== 'string') {
      return config.model;
    }
    if (typeof config.model === 'string') {
      return await this.config.modelProvider.getModel(config.model);
    }
    const resolved = await this.#resolveModelForAgent(agent);
    return resolved.model;
  }

  async #resolveCompactionModel(
    agent: Agent<any, any>,
    fallbackModel: Model | undefined,
    config: ResolvedLocalCompactionConfig,
  ): Promise<Model> {
    if (config.model && typeof config.model !== 'string') {
      return config.model;
    }
    if (typeof config.model === 'string') {
      return await this.config.modelProvider.getModel(config.model);
    }
    if (fallbackModel) {
      return fallbackModel;
    }
    const resolved = await this.#resolveModelForAgent(agent);
    return resolved.model;
  }

  async #getResponseWithReactiveCompaction<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(options: {
    state: RunState<TContext, TAgent>;
    agent: Agent<TContext, AgentOutputType>;
    options: SharedRunOptions<TContext, TAgent>;
    preparedCall: PreparedModelCall<TContext>;
    localCompactionRuntime?: LocalCompactionRuntime;
  }): Promise<{
    response: ModelResponse;
    preparedCall: PreparedModelCall<TContext>;
  }> {
    const requestFor = (preparedCall: PreparedModelCall<TContext>) => ({
      systemInstructions: preparedCall.modelInput.instructions,
      // prompt: preparedCall.prompt,
      // Explicit agent/run config models should take precedence over prompt defaults.
      ...(preparedCall.explictlyModelSet ? { overridePromptModel: true } : {}),
      input: preparedCall.modelInput.input,
      previousResponseId: preparedCall.previousResponseId,
      conversationId: preparedCall.conversationId,
      modelSettings: preparedCall.modelSettings,
      tools: preparedCall.serializedTools,
      toolsExplicitlyProvided: preparedCall.toolsExplicitlyProvided,
      outputType: convertAgentOutputTypeToSerializable(
        options.state._currentAgent.outputType,
      ),
      handoffs: preparedCall.serializedHandoffs,
      tracing: getTracing(
        this.config.tracingDisabled,
        this.config.traceIncludeSensitiveData,
      ),
      signal: options.options.signal,
    });

    try {
      return {
        response: await getResponseWithRetry(
          options.preparedCall.model,
          requestFor(options.preparedCall),
        ),
        preparedCall: options.preparedCall,
      };
    } catch (error) {
      if (
        !isContextLengthExceededError(error) ||
        !options.localCompactionRuntime?.enabled
      ) {
        throw error;
      }
      const compactedCall = await this.#maybeCompactPreparedModelCall({
        state: options.state,
        agent: options.agent,
        options: options.options,
        preparedCall: options.preparedCall,
        localCompactionRuntime: options.localCompactionRuntime,
        triggerOverride: 'reactive',
        force: true,
      });
      if (compactedCall === options.preparedCall) {
        throw error;
      }
      return {
        response: await getResponseWithRetry(
          compactedCall.model,
          requestFor(compactedCall),
        ),
        preparedCall: compactedCall,
      };
    }
  }

  async #resolveSandboxRuntimeModelForAgent<TContext>(
    agent: Agent<TContext, AgentOutputType>,
  ): Promise<SandboxRuntimeModel | undefined> {
    if (!isSandboxRuntimeAgent(agent)) {
      return this.config.model;
    }

    const resolved = await this.#resolveModelForAgent(agent);
    if (
      resolved.resolvedModelName &&
      resolved.resolvedModelName.trim().length > 0
    ) {
      return {
        model: resolved.resolvedModelName,
        modelInstance: resolved.model,
      };
    }

    return resolved.model;
  }

  #getAgentToolParentRunConfig<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(options: SharedRunOptions<TContext, TAgent>): Partial<RunConfig> {
    return typeof options.sandbox === 'undefined'
      ? this.config
      : {
          ...this.config,
          sandbox: options.sandbox,
        };
  }

  /*
   * 应用调用级过滤器并合并会话更新，使模型请求与我们所持久化的历史记录完全一致。
   */
  async #prepareModelCall<
    TContext,
    TAgent extends Agent<TContext, AgentOutputType>,
  >(
    state: RunState<TContext, TAgent>,
    executionAgent: Agent<TContext, AgentOutputType>,
    options: SharedRunOptions<TContext, TAgent>,
    artifacts: AgentArtifacts<TContext>,
    turnInput: AgentInputItem[],
    serverConversationTracker?: ServerConversationTracker,
    sessionInputUpdate?: (
      sourceItems: (AgentInputItem | undefined)[],
      filteredItems?: AgentInputItem[],
    ) => void,
  ): Promise<PreparedModelCall<TContext>> {
    const { model, modelMetadata, explictlyModelSet, resolvedModelName } =
      await this.#resolveModelForAgent(executionAgent);

    const hasExplicitAgentModelSettings =
      executionAgent.hasExplicitModelSettings();
    const agentModelSettings = hasExplicitAgentModelSettings
      ? executionAgent.modelSettings
      : undefined;
    const implicitModelSettings = hasExplicitAgentModelSettings
      ? undefined
      : getImplicitModelSettingsForResolvedModel(
          explictlyModelSet,
          resolvedModelName,
        );

    let modelSettings = mergeModelSettings(
      implicitModelSettings,
      this.config.modelSettings,
    );
    modelSettings = mergeModelSettings(modelSettings, agentModelSettings);
    modelSettings = adjustModelSettingsForNonGPT5RunnerModel(
      explictlyModelSet,
      agentModelSettings ?? implicitModelSettings ?? {},
      model,
      modelSettings,
      resolvedModelName,
    );
    modelSettings = maybeResetToolChoice(
      state._currentAgent,
      state._toolUseTracker,
      modelSettings,
    );
    state._lastModelSettings = modelSettings;

    const systemInstructions = await executionAgent.getSystemPrompt(
      state._context,
    );
    // const prompt = await executionAgent.getPrompt(state._context);

    const { modelInput, sourceItems, persistedItems, filterApplied } =
      await applyCallModelInputFilter(
        state._currentAgent,
        options.callModelInputFilter,
        state._context,
        turnInput,
        systemInstructions,
      );

    // Provide filtered clones whenever filters run so session history mirrors the model payload.
    // Returning an empty array is intentional: it tells the session layer to persist "nothing"
    // instead of falling back to the unfiltered originals when the filter redacts everything.
    sessionInputUpdate?.(
      sourceItems,
      filterApplied ? persistedItems : undefined,
    );

    const previousResponseId =
      serverConversationTracker?.previousResponseId ??
      options.previousResponseId;
    const conversationId =
      serverConversationTracker?.conversationId ?? options.conversationId;

    return {
      ...artifacts,
      model,
      modelMetadata,
      explictlyModelSet,
      modelSettings,
      modelInput,
      // prompt,
      previousResponseId,
      conversationId,
      sourceItems,
      filterApplied,
      turnInput,
    };
  }

  /**
   * @internal
   * Resolves the effective model once so both run loops obey the same precedence rules.
   */
  async #resolveModelForAgent<TContext>(
    agent: Agent<TContext, AgentOutputType>,
  ): Promise<{
    model: Model;
    modelMetadata?: ModelMetadata;
    explictlyModelSet: boolean;
    resolvedModelName?: string;
  }> {
    const explictlyModelSet =
      (agent.model !== undefined &&
        agent.model !== Agent.DEFAULT_MODEL_PLACEHOLDER) ||
      (this.config.model !== undefined &&
        this.config.model !== Agent.DEFAULT_MODEL_PLACEHOLDER);
    const selectedModel = selectModel(agent.model, this.config.model);
    if (typeof selectedModel !== 'string') {
      return {
        model: selectedModel,
        modelMetadata: getModelInstanceMetadata(selectedModel),
        explictlyModelSet,
      };
    }

    const providerDefaultModel =
      selectedModel === Agent.DEFAULT_MODEL_PLACEHOLDER
        ? this.config.modelProvider.getDefaultModelName?.()
        : undefined;
    const selectedModelName =
      providerDefaultModel && providerDefaultModel.trim().length > 0
        ? providerDefaultModel
        : selectedModel;
    const resolvedModelName =
      selectedModelName.trim().length > 0 ? selectedModelName : undefined;
    const resolvedModel =
      await this.config.modelProvider.getModel(selectedModelName);
    const providerMetadata =
      await this.config.modelProvider.getModelMetadata?.(selectedModelName);
    const modelMetadata = mergeModelMetadata(
      providerMetadata,
      getModelInstanceMetadata(resolvedModel),
    );
    return {
      model: resolvedModel,
      modelMetadata,
      explictlyModelSet,
      resolvedModelName,
    };
  }
}

/**
 * 使用共享的默认 `Runner` 实例执行智能体工作流。
 *
 * @param agent - 要调用的入口智能体。
 * @param input - 一个字符串语句、结构化的输入项，或一个恢复运行的 `RunState`。
 * @param options - 控制流式模式、上下文、会话处理以及轮次上限。
 * @returns 当 `stream` 为 false 时返回 `RunResult`，否则返回 `StreamedRunResult`。
 */
export async function run<TAgent extends Agent<any, any>, TContext = undefined>(
  agent: TAgent,
  input: string | AgentInputItem[] | RunState<TContext, TAgent>,
  options?: NonStreamRunOptions<TContext, TAgent>,
): Promise<RunResult<TContext, TAgent>>;
export async function run<TAgent extends Agent<any, any>, TContext = undefined>(
  agent: TAgent,
  input: string | AgentInputItem[] | RunState<TContext, TAgent>,
  options?: StreamRunOptions<TContext, TAgent>,
): Promise<StreamedRunResult<TContext, TAgent>>;
export async function run<TAgent extends Agent<any, any>, TContext = undefined>(
  agent: TAgent,
  input: string | AgentInputItem[] | RunState<TContext, TAgent>,
  options?:
    | StreamRunOptions<TContext, TAgent>
    | NonStreamRunOptions<TContext, TAgent>,
): Promise<RunResult<TContext, TAgent> | StreamedRunResult<TContext, TAgent>> {
  const runner = getDefaultRunner();
  if (options?.stream) {
    return await runner.run(agent, input, options);
  } else {
    return await runner.run(agent, input, options);
  }
}

let defaultRunner: Runner | undefined;

const getDefaultRunner = (): Runner => {
  if (!defaultRunner) {
    defaultRunner = new Runner();
  }
  return defaultRunner;
};

function resolveSessionMemoryStore(
  config: false | SessionMemoryConfig | undefined,
): SessionMemoryStore | undefined {
  if (config === false) {
    return undefined;
  }
  if (!config) {
    return undefined;
  }
  const maybeStore = config as Partial<SessionMemoryStore>;
  if (
    typeof maybeStore.read === 'function' &&
    typeof maybeStore.write === 'function'
  ) {
    return config as SessionMemoryStore;
  }
  return (
    (config as { store?: SessionMemoryStore }).store ??
    new FileSessionMemoryStore()
  );
}

function getModelInstanceMetadata(model: Model): ModelMetadata | undefined {
  return model.metadata;
}

function mergeModelMetadata(
  base: ModelMetadata | undefined,
  override: ModelMetadata | undefined,
): ModelMetadata | undefined {
  if (!base && !override) {
    return undefined;
  }
  return {
    ...(base ?? {}),
    ...(override ?? {}),
  };
}

function applyModelMetadataToCompactionConfig<
  TConfig extends ResolvedLocalCompactionConfig,
>(config: TConfig, metadata: ModelMetadata | undefined): TConfig {
  if (!metadata?.contextWindowTokens || config.contextWindowTokens) {
    return config;
  }
  return {
    ...config,
    contextWindowTokens: metadata.contextWindowTokens,
  };
}

function isContextLengthExceededError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const record = error as Record<string, unknown>;
  const values = [
    record.name,
    record.message,
    record.code,
    record.type,
    record.status,
    record.statusCode,
  ]
    .filter((value): value is string | number => {
      return typeof value === 'string' || typeof value === 'number';
    })
    .join(' ')
    .toLowerCase();

  return (
    /\bcontext[_ -]?length\b/u.test(values) ||
    /maximum context/u.test(values) ||
    /too many tokens/u.test(values) ||
    /token limit/u.test(values) ||
    /input is too long/u.test(values)
  );
}

type PartialAbortOutputPart =
  | { kind: 'message'; text: string }
  | { kind: 'reasoning'; partId?: string; text: string };

function pruneIncompleteToolCallsForExternalAbort(
  items: RunItem[],
  alreadyPersistedCount: number,
): RunItem[] {
  const completedFunctionCallIds = new Set<string>();
  for (const item of items) {
    if (!(item instanceof RunToolCallOutputItem)) continue;
    if (item.rawItem?.status === 'incomplete') continue;
    const callId = item.callId;
    if (callId) {
      completedFunctionCallIds.add(callId);
    }
  }

  return items.filter((item, index) => {
    if (index < alreadyPersistedCount) return true;

    if (
      item instanceof RunToolCallOutputItem &&
      item.rawItem?.status === 'incomplete'
    ) {
      return false;
    }

    if (!(item instanceof RunToolCallItem)) {
      return true;
    }

    const rawItem = item.rawItem as { type?: unknown } | undefined;
    if (rawItem?.type !== 'function_call') {
      return true;
    }

    const callId = item.callId;
    return !callId || completedFunctionCallIds.has(callId);
  });
}

function buildPartialAbortModelResponse(
  events: ResponseStreamEvent[],
): { response: ModelResponse; outputText: string } | undefined {
  const parts: PartialAbortOutputPart[] = [];
  let activePart:
    | { kind: 'message'; index: number; partId?: string }
    | { kind: 'reasoning'; index: number; partId?: string }
    | { kind: 'tool'; partId?: string }
    | undefined;

  const addMessagePart = (partId?: string) => {
    parts.push({ kind: 'message', text: '' });
    activePart = { kind: 'message', index: parts.length - 1, partId };
    return parts[parts.length - 1] as Extract<
      PartialAbortOutputPart,
      { kind: 'message' }
    >;
  };
  const addReasoningPart = (partId?: string) => {
    parts.push({ kind: 'reasoning', partId, text: '' });
    activePart = { kind: 'reasoning', index: parts.length - 1, partId };
    return parts[parts.length - 1] as Extract<
      PartialAbortOutputPart,
      { kind: 'reasoning' }
    >;
  };
  const currentMessagePart = () => {
    if (activePart?.kind === 'message') {
      const part = parts[activePart.index];
      if (part?.kind === 'message') return part;
    }
    return addMessagePart();
  };
  const currentReasoningPart = () => {
    if (activePart?.kind === 'reasoning') {
      const part = parts[activePart.index];
      if (part?.kind === 'reasoning') return part;
    }
    return addReasoningPart();
  };

  for (const event of events) {
    switch (event.type) {
      case 'output_part_started':
        if (event.part.type === 'output_text') {
          addMessagePart(event.partId);
        } else if (event.part.type === 'reasoning') {
          addReasoningPart(event.partId);
        } else {
          activePart = { kind: 'tool', partId: event.partId };
        }
        break;
      case 'output_text_delta':
        currentMessagePart().text += event.delta;
        break;
      case 'reasoning_delta':
        currentReasoningPart().text += event.delta;
        break;
      case 'output_part_done':
        if (activePart?.partId === event.partId) {
          activePart = undefined;
        }
        break;
      default:
        break;
    }
  }

  const output: AgentOutputItem[] = [];
  const outputText: string[] = [];
  for (const part of parts) {
    if (part.kind === 'message') {
      if (!part.text) continue;
      outputText.push(part.text);
      output.push({
        type: 'message',
        role: 'assistant',
        status: 'incomplete',
        content: [{ type: 'output_text', text: part.text }],
      });
      continue;
    }

    if (!part.text) continue;
    output.push({
      type: 'reasoning',
      ...(part.partId ? { id: part.partId } : {}),
      content: [],
      rawContent: [{ type: 'reasoning_text', text: part.text }],
    });
  }

  if (output.length === 0) {
    return undefined;
  }

  return {
    response: {
      output,
      usage: new Usage(),
    },
    outputText: outputText.join(''),
  };
}

export type ToolErrorFormatterArgs<
  TContext = unknown,
  TKind extends 'approval_rejected' = 'approval_rejected',
> = {
  /**
   * The category of tool error being formatted.
   */
  kind: TKind;
  /**
   * The tool runtime that produced the error.
   */
  toolType: 'function' | 'computer' | 'shell';
  /**
   * The name of the tool that produced the error.
   */
  toolName: string;
  /**
   * The unique tool call identifier.
   */
  callId: string;
  /**
   * The SDK's default message for this error kind.
   */
  defaultMessage: string;
  /**
   * The active run context for the current execution.
   */
  runContext: RunContext<TContext>;
};

export type ToolErrorFormatter<TContext = unknown> = (
  args: ToolErrorFormatterArgs<TContext>,
) => Promise<string | undefined> | string | undefined;

function getImplicitModelSettingsForResolvedModel(
  explictlyModelSet: boolean,
  resolvedModelName?: string,
): ModelSettings {
  if (resolvedModelName && resolvedModelName.trim().length > 0) {
    return getDefaultModelSettings(resolvedModelName);
  }
  if (explictlyModelSet) {
    return {};
  }
  return getDefaultModelSettings();
}
